import { describeApiError } from '@brush/api-client';
import { useState } from 'react';
import { ownCardFor } from '../ownCard';
import { usePlayer } from '../player';
import { useScreenViewed } from '../api';
import {
  NOT_YOURS,
  STATS_PAGE_SIZE,
  basisLine,
  buyInLine,
  buyInsCoverage,
  dollars,
  net,
  pageCount,
  placeLabel,
  readState,
} from '../ownResults';
import { useOwnResults } from '../queries';

/**
 * The caller's own results at the room on screen (known gap 78): the header's figures, buy-ins with
 * re-entries and net, and the whole history a page at a time. Every figure comes from
 * `GET /api/rooms/{roomId}/players/me/results`, whose cashes, wins and winnings are the player read's own —
 * so the one winnings figure here and Home's header cannot disagree (known gap 95).
 */
export function StatsView() {
  const { player, room, theme, readOnly, membership } = usePlayer();
  useScreenViewed('stats', player?.roomId ?? null);
  const roomId = room?.id ?? null;
  // The page belongs to the room it was turned in: another card on screen starts at its newest results.
  const [paging, setPaging] = useState({ roomId, page: 1 });
  const page = paging.roomId === roomId ? paging.page : 1;
  const setPage = (to: (p: number) => number) => setPaging({ roomId, page: to(page) });
  const resultsQ = useOwnResults(roomId, page, STATS_PAGE_SIZE, !readOnly);

  if (!player) return null;

  // The holder's own card, from their membership, and only beside their own player (#494): the player read carries
  // no card, and a member of staff browsing somebody else's stats sees none.
  const ownCard = ownCardFor(player, membership);

  const results = readState({
    readOnly,
    isPending: resultsQ.isPending,
    failure: resultsQ.isError ? describeApiError(resultsQ.error) : null,
    data: resultsQ.data,
  });
  const totals = results.kind === 'ready' ? results.data.totals : null;
  const pages = results.kind === 'ready' ? pageCount(results.data.total, STATS_PAGE_SIZE) : 1;

  const card = { background: theme.panel, border: `1px solid ${theme.border}` };
  const muted = { color: theme.textMuted };
  // A figure from the read: … while it is on its way, — when it failed or is not this caller's (gap 93).
  const pending = results.kind === 'loading' ? '…' : '—';

  const stat = (label: string, value: string, hint?: string) => (
    <div style={card} className="rounded-xl p-5">
      <div className="text-xs uppercase tracking-wider" style={muted}>
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      {hint ? (
        <div className="mt-1 text-xs" style={muted}>
          {hint}
        </div>
      ) : null}
    </div>
  );

  const netValue = totals ? net(totals) : null;
  const coverage = totals ? buyInsCoverage(totals) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">My stats</h1>
        <p className="mt-1 text-sm" style={muted}>
          {player.name}
          {ownCard === null ? null : ` · ${ownCard}`}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stat('Winnings', dollars(player.totalWinnings))}
        {stat('Wins', String(player.wins))}
        {stat('Cashes', String(player.cashes))}
        {stat('Best finish', totals ? (totals.bestFinish === null ? '—' : `#${totals.bestFinish}`) : pending)}
      </div>

      <section style={card} className="rounded-xl p-6">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
          This room
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wider" style={muted}>
              Buy-ins
            </div>
            <div className="mt-1 text-xl font-bold">{totals ? dollars(totals.buyInsSpent) : pending}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider" style={muted}>
              Net
            </div>
            <div
              className="mt-1 text-xl font-bold"
              // A dash is not a figure about anybody, so it takes the ordinary text colour: on this ground
              // orange means "this is you", and here it would sit on nothing — on somebody else's portal, too.
              style={{ color: netValue === null ? theme.text : netValue >= 0 ? theme.accent : '#f87171' }}
            >
              {netValue === null ? pending : `${netValue >= 0 ? '+' : '−'}${dollars(Math.abs(netValue))}`}
            </div>
          </div>
        </div>
        {totals ? (
          <p className="mt-4 text-xs" style={muted}>
            {basisLine(totals)}
            {coverage ? ` ${coverage}` : ''}
          </p>
        ) : null}
      </section>

      <section style={card} className="overflow-hidden rounded-xl">
        <header className="p-5" style={{ borderBottom: `1px solid ${theme.border}` }}>
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
            Full history
          </h2>
        </header>
        {results.kind === 'loading' ? (
          <p className="p-8 text-center text-sm" style={muted}>
            Loading your results…
          </p>
        ) : results.kind === 'failed' ? (
          <p className="p-8 text-center text-sm" style={muted}>
            Your results couldn't be loaded. {results.message}
          </p>
        ) : results.kind === 'notYours' ? (
          <p className="p-8 text-center text-sm" style={muted}>
            {NOT_YOURS}
          </p>
        ) : results.data.total === 0 ? (
          <p className="p-8 text-center text-sm" style={muted}>
            No recorded results yet.
          </p>
        ) : (
          <>
            <ul style={{ opacity: resultsQ.isPlaceholderData ? 0.5 : 1 }}>
              {results.data.data.map((r) => (
                <li
                  key={r.tournamentId}
                  className="flex items-center justify-between px-5 py-3"
                  style={{ borderBottom: `1px solid ${theme.border}` }}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                      style={{
                        background: r.position === 1 ? theme.accent : theme.border,
                        color: r.position === 1 ? theme.bg : theme.text,
                      }}
                    >
                      {placeLabel(r.position)}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-semibold">{r.name}</div>
                      <div className="text-xs" style={muted}>
                        {r.date} · {buyInLine(r.buyIn, r.buyins)}
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 font-bold" style={{ color: theme.accent }}>
                    {dollars(r.prize)}
                  </div>
                </li>
              ))}
            </ul>
            {pages > 1 ? (
              <nav className="flex items-center justify-between px-5 py-3 text-sm" aria-label="History pages">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="font-semibold disabled:opacity-40"
                  style={{ color: theme.accent }}
                >
                  ← Newer
                </button>
                <span style={muted}>
                  Page {results.data.page} of {pages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page >= pages}
                  className="font-semibold disabled:opacity-40"
                  style={{ color: theme.accent }}
                >
                  Older →
                </button>
              </nav>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
